/**
 * MobileCardList — Native mobile card-list replacement for antd Table.
 *
 * Renders each dataSource record as a card with label-value fields,
 * a search bar, sort selector, refresh button, pagination, row selection,
 * and expandable rows.  Designed to drop in wherever a plain <Table> is used.
 *
 * Props are a subset of antd Table + a few mobile-specific additions.
 */
import React, { useState, useMemo, useCallback, useEffect, useRef } from "react";
import { Input, Button, Checkbox, Spin, Empty, Pagination } from "antd";
import {
  SearchOutlined,
  SortAscendingOutlined,
  ReloadOutlined,
  LoadingOutlined,
} from "@ant-design/icons";
import "./styles.css";
import Select from "@components/ResponsiveSelect";

/* ---------- helpers ---------- */

const getRowKey = (record, rowKey, index) => {
  if (typeof rowKey === "function") return rowKey(record);
  if (typeof rowKey === "string") return record[rowKey];
  return String(index);
};

/** Resolve nested dataIndex like "user.name" from record */
const getNestedValue = (obj, path) => {
  if (!path) return undefined;
  if (typeof path !== "string") return obj[path];
  return path.split(".").reduce((acc, key) => (acc != null ? acc[key] : undefined), obj);
};

const getCellValue = (record, col, index, action) => {
  const rawValue = col.dataIndex ? getNestedValue(record, col.dataIndex) : undefined;

  // ProTable valueEnum mapping
  if (col.valueEnum && rawValue != null) {
    const entry = col.valueEnum[rawValue];
    if (entry && typeof entry === "object") return entry.text ?? rawValue;
    if (typeof entry === "string") return entry;
  }

  if (col.render) return col.render(rawValue, record, index, action);

  if (col.dataIndex) {
    if (rawValue === undefined || rawValue === null) return "-";
    if (typeof rawValue === "boolean") return String(rawValue);
    return rawValue;
  }
  return null;
};

/* ---------- component ---------- */

const MobileCardList = (props) => {
  const {
    columns = [],
    dataSource = [],
    loading = false,
    rowKey = "id",
    rowSelection,
    pagination,
    onChange,
    expandable,
    onRow,
    title,
    footer,
    className = "",
    style,
    size = "default",
    locale,
    showToolbar = true,
    onRefresh,
    mobileRowActions,
    mobileInfiniteScroll = false,
    onLoadMore,
    hasMore = false,
    loadingMore = false,
    loadMoreError = false,
    topToolbar,
    tableAction,
    mobileCardCollapsible = true,
  } = props;

  /* ---- local state ---- */
  const [searchText, setSearchText] = useState("");
  const [sortField, setSortField] = useState(undefined);
  const [sortOrder, setSortOrder] = useState(undefined); // 'ascend' | 'descend'
  const [currentPage, setCurrentPage] = useState(1);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [cardExpandedKeys, setCardExpandedKeys] = useState([]);

  const emptyText = locale?.emptyText ?? "No data";

  /* ---- classify columns ---- */
  const { titleCol, infoCols, actionCols } = useMemo(() => {
    let titleCol = null;
    const infoCols = [];
    const actionCols = [];

    columns.forEach((col) => {
      // skip selection & expand columns
      if (col.key === "selection" || col.key === "expand") return;
      if (col.type === "selection" || col.type === "expand") return;
      if (col.dataIndex === "ant-table-selection-column" || col.dataIndex === "ant-table-expand-icon") return;

      // ProTable hideInTable/index
      if (col.hideInTable) return;
      if (col.valueType === "index" || col.valueType === "indexBorder") return;

      // ProTable option columns are row actions.
      if (col.valueType === "option") {
        actionCols.push(col);
        return;
      }

      // Detect action column: title is empty and has a render function
      if (!col.title && col.render && !col.dataIndex) {
        actionCols.push(col);
        return;
      }

      // First column with dataIndex becomes title
      if (!titleCol && (col.dataIndex || col.render)) {
        titleCol = col;
        return;
      }

      if (col.dataIndex || col.render) {
        infoCols.push(col);
      }
    });

    return { titleCol, infoCols, actionCols };
  }, [columns]);

  /* ---- sortable columns for dropdown ---- */
  const sortableColumns = useMemo(
    () => columns.filter((c) => c.sorter && c.title),
    [columns]
  );

  /* ---- filter & sort data ---- */
  const processedData = useMemo(() => {
    let result = [...dataSource];

    // local search
    if (searchText.trim()) {
      const q = searchText.toLowerCase();
      result = result.filter((record) =>
        columns.some((col) => {
          const val = getCellValue(record, col, 0, tableAction);
          return val != null && String(val).toLowerCase().includes(q);
        })
      );
    }

    // sort
    if (sortField) {
      const col = columns.find((c) => c.dataIndex === sortField || c.key === sortField);
      if (col?.sorter) {
        if (typeof col.sorter === "function") {
          result.sort((a, b) => {
            const r = col.sorter(a, b);
            return sortOrder === "descend" ? -r : r;
          });
        } else if (col.sorter === true) {
          result.sort((a, b) => {
            const va = a[sortField] ?? "";
            const vb = b[sortField] ?? "";
            const cmp = String(va).localeCompare(String(vb), undefined, { numeric: true });
            return sortOrder === "descend" ? -cmp : cmp;
          });
        }
      }
    }

    return result;
  }, [dataSource, searchText, sortField, sortOrder, columns]);

  /* ---- pagination ---- */
  const isRemotePagination =
    pagination !== false &&
    pagination &&
    typeof pagination.onChange === "function";
  const pageSize = pagination?.pageSize ?? pagination?.defaultPageSize ?? 20;
  const totalItems = isRemotePagination
    ? pagination.total ?? processedData.length
    : processedData.length;
  const totalPages = Math.ceil(totalItems / pageSize);
  const pagedData = useMemo(() => {
    if (pagination === false || isRemotePagination || mobileInfiniteScroll) {
      return processedData;
    }
    const start = (currentPage - 1) * pageSize;
    return processedData.slice(start, start + pageSize);
  }, [processedData, currentPage, pageSize, pagination, isRemotePagination, mobileInfiniteScroll]);

  /* ---- selection ---- */
  const selectedKeys = rowSelection?.selectedRowKeys ?? [];
  const onSelectChange = rowSelection?.onChange;

  const getRowsByKeys = useCallback(
    (keys) => processedData.filter((record, index) =>
      keys.includes(getRowKey(record, rowKey, index))
    ),
    [processedData, rowKey]
  );

  const handleSelect = useCallback(
    (key, checked) => {
      if (!onSelectChange) return;
      const next = checked
        ? [...selectedKeys, key]
        : selectedKeys.filter((k) => k !== key);
      onSelectChange(next, getRowsByKeys(next));
    },
    [selectedKeys, onSelectChange, getRowsByKeys]
  );

  const handleSelectAll = useCallback(
    (checked) => {
      if (!onSelectChange) return;
      const next = checked ? pagedData.map((r, i) => getRowKey(r, rowKey, i)) : [];
      onSelectChange(next, checked ? pagedData : []);
    },
    [pagedData, rowKey, onSelectChange]
  );

  /* ---- expand ---- */
  const handleExpand = useCallback(
    (key) => {
      setExpandedKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    },
    []
  );

  const handleCardExpand = useCallback(
    (key) => {
      if (!mobileCardCollapsible) return;
      setCardExpandedKeys((prev) =>
        prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
      );
    },
    [mobileCardCollapsible]
  );

  /* ---- sort change ---- */
  const handleSortChange = useCallback(
    (value) => {
      if (!value) {
        setSortField(undefined);
        setSortOrder(undefined);
      } else {
        // value format: "field:order"
        const [field, order] = value.split(":");
        setSortField(field);
        setSortOrder(order);
      }
      // fire onChange for compatibility
      if (onChange) {
        onChange(
          { current: currentPage, pageSize },
          {},
          value ? { field: value.split(":")[0], order: value.split(":")[1] } : {}
        );
      }
    },
    [onChange, currentPage, pageSize]
  );

  const loadMoreRef = useRef(null);
  useEffect(() => {
    if (!mobileInfiniteScroll || !onLoadMore || typeof IntersectionObserver === "undefined") {
      return undefined;
    }
    const node = loadMoreRef.current;
    if (!node) return undefined;
    const observer = new IntersectionObserver((entries) => {
      const first = entries[0];
      if (first?.isIntersecting && hasMore && !loading && !loadingMore) {
        onLoadMore();
      }
    }, { rootMargin: "160px 0px" });
    observer.observe(node);
    return () => observer.disconnect();
  }, [mobileInfiniteScroll, onLoadMore, hasMore, loading, loadingMore]);

  /* ---- render ---- */
  const isCompact = size === "small";

  return (
    <div className={`mobile-card-list ${className}`} style={style}>
      {topToolbar && (
        <div className="mcl-top-toolbar">
          {topToolbar}
        </div>
      )}

      {/* ---- Toolbar ---- */}
      {showToolbar && (
        <div className="mcl-toolbar">
          <Input
            prefix={<SearchOutlined />}
            placeholder="Search..."
            value={searchText}
            onChange={(e) => {
              setSearchText(e.target.value);
              setCurrentPage(1);
            }}
            allowClear
            className="mcl-search"
          />
          <div className="mcl-toolbar-actions">
            {sortableColumns.length > 0 && (
              <Select
                allowClear
                placeholder="Sort"
                value={sortField ? `${sortField}:${sortOrder}` : undefined}
                onChange={handleSortChange}
                className="mcl-sort-select"
                suffixIcon={<SortAscendingOutlined />}
                options={sortableColumns.flatMap((col) => [
                  {
                    label: `${col.title} ↑`,
                    value: `${col.dataIndex || col.key}:ascend`,
                  },
                  {
                    label: `${col.title} ↓`,
                    value: `${col.dataIndex || col.key}:descend`,
                  },
                ])}
              />
            )}
            {onRefresh && (
              <Button
                icon={<ReloadOutlined spin={loading} />}
                onClick={onRefresh}
                disabled={loading}
                className="mcl-refresh-btn"
              />
            )}
          </div>
        </div>
      )}

      {/* ---- Title (from original table title prop) ---- */}
      {typeof title === "function" ? title(pagedData) : title}

      {/* ---- Select all ---- */}
      {rowSelection && pagedData.length > 0 && (
        <div className="mcl-select-all">
          <Checkbox
            checked={pagedData.length > 0 && pagedData.every((r, i) => selectedKeys.includes(getRowKey(r, rowKey, i)))}
            indeterminate={
              pagedData.some((r, i) => selectedKeys.includes(getRowKey(r, rowKey, i))) &&
              !pagedData.every((r, i) => selectedKeys.includes(getRowKey(r, rowKey, i)))
            }
            onChange={(e) => handleSelectAll(e.target.checked)}
          >
            Select all
          </Checkbox>
          <span className="mcl-selected-count">
            {selectedKeys.length > 0 && `${selectedKeys.length} selected`}
          </span>
        </div>
      )}

      {/* ---- Card List ---- */}
      <Spin spinning={loading} indicator={<LoadingOutlined />}>
        <div className="mcl-card-list">
          {pagedData.length === 0 && !loading ? (
            <Empty description={emptyText} className="mcl-empty" />
          ) : (
            pagedData.map((record, index) => {
              const key = getRowKey(record, rowKey, index);
              const isExpanded = expandedKeys.includes(key);
              const isCardExpanded = !mobileCardCollapsible || cardExpandedKeys.includes(key);
              const isSelected = selectedKeys.includes(key);
              const rowProps = onRow ? onRow(record, index) : {};
              const extraActions =
                typeof mobileRowActions === "function"
                  ? mobileRowActions(record, index, {
                      rowKey: key,
                      selected: isSelected,
                      expanded: isCardExpanded,
                    }) || []
                  : [];
              const rowActions = [
                ...actionCols.map((col) => getCellValue(record, col, index, tableAction)),
                ...React.Children.toArray(extraActions),
              ].filter((action) => action !== null && action !== undefined && action !== false);

              return (
                <div
                  key={key}
                  className={`mcl-card ${isSelected ? "mcl-card-selected" : ""} ${isCompact ? "mcl-card-compact" : ""} ${rowProps.className || ""}`}
                  onClick={rowProps.onClick}
                >
                  {/* Card Header */}
                  <div
                    className={`mcl-card-header ${mobileCardCollapsible ? "mcl-card-header-clickable" : ""}`}
                    onClick={(e) => {
                      e.stopPropagation();
                      if (e.target.closest("button") || e.target.closest("input") || e.target.closest("a")) {
                        return;
                      }
                      handleCardExpand(key);
                    }}
                  >
                    {rowSelection && (
                      <Checkbox
                        checked={isSelected}
                        onChange={(e) => {
                          e.stopPropagation();
                          handleSelect(key, e.target.checked);
                        }}
                        onClick={(e) => e.stopPropagation()}
                      />
                    )}
                    <span className="mcl-card-title">
                      {titleCol ? getCellValue(record, titleCol, index, tableAction) : key}
                    </span>
                    {mobileCardCollapsible && (
                      <Button
                        type="text"
                        size="small"
                        className="mcl-expand-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleCardExpand(key);
                        }}
                        icon={
                          <span className={`mcl-expand-icon ${isCardExpanded ? "mcl-expanded" : ""}`}>
                            ›
                          </span>
                        }
                        aria-label={isCardExpanded ? "Collapse card" : "Expand card"}
                      />
                    )}
                    {expandable && isCardExpanded && (
                      <Button
                        type="text"
                        size="small"
                        className="mcl-expand-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleExpand(key);
                        }}
                        icon={
                          <span className={`mcl-expand-icon ${isExpanded ? "mcl-expanded" : ""}`}>
                            ›
                          </span>
                        }
                      />
                    )}
                  </div>

                  {/* Card Body — label : value rows */}
                  {isCardExpanded && infoCols.length > 0 && (
                    <div className="mcl-card-body">
                      {infoCols.map((col) => (
                        <div key={col.key || col.dataIndex} className="mcl-field">
                          <span className="mcl-field-label">{col.title}</span>
                          <span className="mcl-field-value">
                            {getCellValue(record, col, index, tableAction)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}

                  {/* Card Footer — action buttons */}
                  {isCardExpanded && rowActions.length > 0 && (
                    <div className="mcl-card-footer">
                      {rowActions.map((action, actionIndex) => (
                        <span
                          key={action?.key || actionIndex}
                          className="mcl-action"
                          onClick={(e) => e.stopPropagation()}
                        >
                          {action}
                        </span>
                      ))}
                    </div>
                  )}

                  {/* Expandable content */}
                  {isCardExpanded && expandable && isExpanded && (
                    <div className="mcl-card-expanded">
                      {expandable.expandedRowRender
                        ? expandable.expandedRowRender(record, index, 0, isExpanded)
                        : null}
                    </div>
                  )}
                </div>
              );
            })
          )}
        </div>
      </Spin>

      {mobileInfiniteScroll && (
        <div className="mcl-infinite-status" ref={loadMoreRef}>
          {loadingMore ? (
            <span>Loading...</span>
          ) : loadMoreError ? (
            <Button size="small" onClick={onLoadMore}>
              Retry
            </Button>
          ) : hasMore ? (
            <span>Pull up to load more</span>
          ) : pagedData.length > 0 ? (
            <span>No more data</span>
          ) : null}
        </div>
      )}

      {/* ---- Pagination ---- */}
      {!mobileInfiniteScroll && pagination !== false && totalPages > 1 && (
        <div className="mcl-pagination">
          <Pagination
            current={isRemotePagination ? pagination.current : currentPage}
            total={totalItems}
            pageSize={pageSize}
            size="small"
            simple
            onChange={(page) => {
              if (isRemotePagination) {
                pagination.onChange(page, pageSize);
              } else {
                setCurrentPage(page);
              }
            }}
            showSizeChanger={false}
          />
        </div>
      )}

      {/* ---- Footer ---- */}
      <div className="mcl-footer">
        {typeof footer === "function" ? footer(pagedData) : footer}
      </div>
    </div>
  );
};

export default MobileCardList;
